import { ageFromDateOfBirth } from './faceIdentityApi';
import {
  enabledRegisterFields,
  labelToClassName,
  normalizeFieldOptions,
  normalizeRegisterForm,
  defaultRegisterForm,
} from './kioskLandingDefaults';
import { MOBILE_10_ERROR, isRegisterMobileComplete, normalizeRegisterMobileValue } from '../../shared/mobileDigits';

export function getRegisterFormConfig(config) {
  return normalizeRegisterForm(config?.registerForm || defaultRegisterForm());
}

export function emptyRegisterValues() {
  return { custom: {} };
}

/** Mobile/name from mobile-check or stylist lookup navigation state. */
export function readRegisterPrefillFromNav(locationState, pending) {
  const st = locationState && typeof locationState === 'object' ? locationState : {};
  const pend = pending && typeof pending === 'object' ? pending : {};
  const mobileRaw =
    st.prefillMobile ||
    st.guestMobile ||
    st.lookupMobile ||
    pend.prefillMobile ||
    pend.guestMobile ||
    pend.lookupMobile ||
    '';
  const nameRaw = st.guestName || pend.guestName || '';
  const genderRaw = st.guestGender || pend.guestGender || '';
  return {
    mobile: mobileRaw ? normalizeRegisterMobileValue(mobileRaw) : '',
    name: String(nameRaw || '').trim(),
    // From an engagement booking ("male" | "female"): preselects the gender option.
    gender: String(genderRaw || '').trim().toLowerCase(),
  };
}

/** Fill register `custom` mobile/name when empty (kiosk field keys). */
export function applyRegisterNavPrefill(values, registerForm, prefill) {
  if (!prefill || (!prefill.mobile && !prefill.name && !prefill.gender)) return values;
  const form = normalizeRegisterForm(registerForm || defaultRegisterForm());
  const enabled = enabledRegisterFields(form);
  const custom = { ...(values?.custom || {}) };
  if (prefill.mobile) {
    const field = enabled.find((f) => f.type === 'mobile');
    const key = field?.key || 'mobile';
    const current = normalizeRegisterMobileValue(custom[key] || '');
    if (!isRegisterMobileComplete(current)) {
      custom[key] = normalizeRegisterMobileValue(prefill.mobile);
    }
  }
  if (prefill.name) {
    const field = enabled.find((f) => f.type === 'name');
    if (field && !String(custom[field.key] || '').trim()) {
      custom[field.key] = prefill.name;
    }
  }
  if (prefill.gender) {
    const field = enabled.find((f) => f.type === 'gender' || f.key === 'gender');
    if (field && !String(custom[field.key] || '').trim()) {
      const option = normalizeFieldOptions(field.options, 'gender').find(
        (opt) => String(opt?.label || '').trim().toLowerCase() === prefill.gender,
      );
      if (option) custom[field.key] = String(option.label).trim();
    }
  }
  return { ...values, custom };
}

export function buildRegisterDraft(values) {
  const custom = values.custom && typeof values.custom === 'object' ? values.custom : {};
  return {
    custom: Object.fromEntries(
      Object.entries(custom).map(([key, val]) => [key, String(val || '').trim()]),
    ),
  };
}

const CFP_ENROLLED = new Set(['enable', 'yes', 'enrolled', 'true', '1']);

/** Stored in draft.custom when Customer First Plan is enabled. */
export const CUSTOMER_FIRST_PLAN_ID_KEY = 'customerFirstPlanId';

/** Stored in draft.custom for plan credit / balance limit. */
export const CUSTOMER_FIRST_CREDIT_KEY = 'customerFirstCreditLimit';

export function isCustomerFirstEnrolled(value) {
  return CFP_ENROLLED.has(String(value || '').trim().toLowerCase());
}

export function resolvePlanCreditLimit(preferences = null, membership = null) {
  const prefs = preferences && typeof preferences === 'object' ? preferences : {};
  for (const key of [
    'customer_first_balance',
    'plan_balance',
    'customerFirstCreditLimit',
    'credit_limit',
    'plan_credit_limit',
  ]) {
    const val = prefs[key];
    if (val != null && String(val).trim() !== '') return String(val).trim();
  }
  if (membership?.points != null && String(membership.points).trim() !== '') {
    return String(membership.points);
  }
  return '';
}

export function resolvePlanId(preferences = null, membership = null) {
  const prefs = preferences && typeof preferences === 'object' ? preferences : {};
  return String(
    prefs.customer_first_plan_id || prefs.plan_id || membership?.member_id || '',
  ).trim();
}

export function customerFirstPlanField(fields, registerForm = null) {
  const form = normalizeRegisterForm(registerForm || { fields });
  return enabledRegisterFields(form).find(
    (f) => f.type === 'membershipTier' || f.key === 'membershipTier',
  );
}

export function validateRegisterDraft({ draft, fields, hasCapture, registerForm }) {
  const form = normalizeRegisterForm(registerForm || { fields });
  const enabled = enabledRegisterFields(form);
  for (const field of enabled) {
    const val = draft.custom?.[field.key] || '';
    const isMobile = field.type === 'mobile' || field.key === 'mobile';
    if (field.required && isMobile) {
      if (!isRegisterMobileComplete(val)) {
        return `${field.label || 'Mobile number'} is required`;
      }
      continue;
    }
    if (field.required && !val) {
      return `${field.label || 'Field'} is required`;
    }
    if (isMobile && val && !isRegisterMobileComplete(val)) {
      return MOBILE_10_ERROR;
    }
  }
  const cfpField = enabled.find(
    (f) => f.type === 'membershipTier' || f.key === 'membershipTier',
  );
  if (cfpField && isCustomerFirstEnrolled(draft.custom?.[cfpField.key])) {
    const planId = String(draft.custom?.[CUSTOMER_FIRST_PLAN_ID_KEY] || '').trim();
    if (!planId) {
      return 'Plan ID is required when Customer First Plan is selected';
    }
  }
  if (!hasCapture) return 'No face capture available — please scan again';
  return '';
}

/** CRM preference key = className (label name) for transfer to another CRM database. */
export function crmClassNameForField(field) {
  return (
    String(field?.className || '').trim() ||
    labelToClassName(field?.label) ||
    String(field?.key || '').trim() ||
    'field'
  );
}

function formatDobDisplay(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  let iso = s;
  let display = s;
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const slashMatch = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch;
    display = `${d}/${m}/${String(y).slice(-2)}`;
    iso = `${y}-${m}-${d}`;
  } else if (slashMatch) {
    let [, d, m, y] = slashMatch;
    if (y.length === 2) y = Number(y) > 50 ? `19${y}` : `20${y}`;
    d = String(d).padStart(2, '0');
    m = String(m).padStart(2, '0');
    display = `${d}/${m}/${String(y).slice(-2)}`;
    iso = `${y}-${m}-${d}`;
  }
  const age = ageFromDateOfBirth(iso);
  if (age != null) return `${display}, ${age}`;
  return display;
}

function welcomeLabelForField(field) {
  if (field?.type === 'name' || field?.key === 'name') return 'Name';
  if (field?.type === 'mobile' || field?.key === 'mobile') return 'Mobile';
  if (field?.type === 'dateOfBirth' || field?.key === 'dateOfBirth') return 'DOB';
  if (field?.type === 'membershipTier' || field?.key === 'membershipTier') {
    return String(field?.label || '').trim() || 'Customer First Plan';
  }
  const label = String(field?.label || '').trim();
  if (label.toLowerCase() === 'mobile number') return 'Mobile';
  if (label.toLowerCase() === 'date of birth') return 'DOB';
  return label || crmClassNameForField(field) || 'Field';
}

function humanizePrefKey(key) {
  return String(key || '')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

/** Profile payload for salon register (SaaS — no Nxt CRM membership/allergy/preference fields). */
export function buildRegisterProfilePayload(draft, fields, registerForm = null) {
  const form = normalizeRegisterForm(registerForm || { fields });
  const enabled = enabledRegisterFields(form);
  let dateOfBirth = '';

  enabled.forEach((field) => {
    const val = draft.custom?.[field.key];
    if (!val) return;
    if (field.type === 'dateOfBirth' || field.key === 'dateOfBirth') {
      dateOfBirth = val;
    }
  });

  const byType = (type) => {
    const match = enabled.find((f) => f.type === type && draft.custom?.[f.key]);
    return match ? draft.custom[match.key] : '';
  };
  const byLabel = (needle) => {
    const match = enabled.find(
      (f) => String(f.label || '').toLowerCase().includes(needle) && draft.custom?.[f.key],
    );
    return match ? draft.custom[match.key] : '';
  };
  const name = byType('name') || byLabel('name') || 'Guest';
  const rawMobile = byType('mobile') || byLabel('mobile') || byLabel('phone') || '';
  const mobile = normalizeRegisterMobileValue(rawMobile) || '+910000000000';
  const gender = byType('gender') || byLabel('gender') || '';
  const email = byType('email') || byLabel('email') || '';
  const skinType = byType('skinType') || byLabel('skin') || '';
  let dateOfBirthIso = dateOfBirth || undefined;
  if (dateOfBirthIso) {
    const slashMatch = String(dateOfBirthIso).match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/);
    if (slashMatch) {
      let [, d, m, y] = slashMatch;
      if (y.length === 2) y = Number(y) > 50 ? `19${y}` : `20${y}`;
      dateOfBirthIso = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
  }

  return {
    name,
    mobile,
    ...(gender ? { gender } : {}),
    ...(email ? { email } : {}),
    ...(skinType ? { skinType } : {}),
    dateOfBirth: dateOfBirthIso,
  };
}

export function confirmRowsFromDraft(draft, fields, registerForm = null) {
  const form = normalizeRegisterForm(registerForm || { fields });
  const enabled = enabledRegisterFields(form);
  return enabled
    .flatMap((field) => {
      const value = draft.custom?.[field.key];
      if (!value) return [];
      if (field.type === 'dateOfBirth') {
        return [{ label: welcomeLabelForField(field), value: formatDobDisplay(value) || value }];
      }
      if (field.type === 'membershipTier') {
        const rows = [];
        if (isCustomerFirstEnrolled(value)) {
          const planId = String(draft.custom?.[CUSTOMER_FIRST_PLAN_ID_KEY] || '').trim();
          const credit = String(draft.custom?.[CUSTOMER_FIRST_CREDIT_KEY] || '').trim();
          rows.push({ label: 'Plan ID', value: planId || '—' });
          rows.push({ label: 'Plan credit limit', value: credit || '—' });
        } else {
          rows.push({ label: field.label || 'Customer First Plan', value: '—' });
        }
        return rows;
      }
      return [{ label: field.label || 'Field', value }];
    })
    .filter(Boolean);
}

const HIDDEN_PREF_KEYS = new Set([
  'preferred_stylist',
  'selected_services',
  'selected_service_ids',
  'date_of_birth',
  'dateofbirth',
  'dob',
  'anniversary_date',
  'anniversarydate',
  'dateOfBirth',
  'membership_tier',
  'tier',
  'plan_id',
  'plan_balance',
  'planid',
  'planbalance',
  'plan_option_id',
  'dpdp_consent',
  'dpdpconsent',
]);

const CFP_META_KEYS = new Set([
  'customer_first_plan_option_id',
  'customer_first_plan_name',
  'customer_first_pay_amount',
  'customer_first_service_value',
  'customer_first_plan_id',
  'customer_first_balance',
  'plan_id',
  'plan_balance',
]);

function preferenceValueForField(preferences, field, user = null, membership = null) {
  if (!field) return '';
  if (preferences && typeof preferences === 'object') {
    const candidates = [
      field.key,
      crmClassNameForField(field),
      field.className,
      field.type,
      labelToClassName(field.label),
    ]
      .map((v) => String(v || '').trim())
      .filter(Boolean);

    if (field.type === 'dateOfBirth' || field.key === 'dateOfBirth') {
      candidates.push('date_of_birth', 'dateOfBirth', 'dob');
    }
    if (field.type === 'membershipTier' || field.key === 'membershipTier') {
      candidates.push(
        'membershipTier',
        'membership_tier',
        'tier',
        'customer_first_plan_name',
      );
    }
    if (field.type === 'gender' || field.key === 'gender') {
      candidates.push('gender', 'Gender');
    }

    for (const key of candidates) {
      const val = preferences[key];
      if (val != null && String(val).trim() !== '') return String(val).trim();
    }
  }

  if (field.type === 'membershipTier' || field.key === 'membershipTier') {
    const planId =
      (preferences &&
        (preferences.customer_first_plan_id || preferences.plan_id || preferences.member_id)) ||
      membership?.member_id ||
      '';
    if (String(planId || '').trim()) return 'Yes';
    if (membership?.tier && String(membership.tier).toLowerCase() !== 'standard') {
      return 'Yes';
    }
  }

  if (user && typeof user === 'object') {
    if (field.type === 'name' || field.key === 'name') {
      const name = user.name || user.full_name || '';
      if (String(name).trim()) return String(name).trim();
    }
    if (field.type === 'mobile' || field.key === 'mobile') {
      const mobile = user.mobile || user.phone || '';
      if (String(mobile).trim()) return String(mobile).trim();
    }
    if (field.type === 'gender' || field.key === 'gender') {
      const gender = user.gender || '';
      if (String(gender).trim()) return String(gender).trim();
    }
  }
  return '';
}

function markFieldKeysUsed(usedKeys, field) {
  [
    crmClassNameForField(field),
    field.key,
    field.type,
    field.className,
    labelToClassName(field.label),
  ]
    .map((v) => String(v || '').trim().toLowerCase())
    .filter(Boolean)
    .forEach((k) => usedKeys.add(k));
}

/**
 * Build Preference rows for welcome from the live Common register form.
 * Customer First Plan shows Plan ID + Plan credit limit (no Enable/Disable wording).
 */
export function preferenceRowsFromRegisterForm(
  preferences,
  registerForm,
  user = null,
  membership = null,
) {
  const form = normalizeRegisterForm(registerForm);
  const fields = enabledRegisterFields(form);
  const rows = [];
  const usedKeys = new Set();
  const prefs = preferences && typeof preferences === 'object' ? preferences : {};

  fields.forEach((field) => {
    markFieldKeysUsed(usedKeys, field);

    if (field.type === 'membershipTier' || field.key === 'membershipTier') {
      CFP_META_KEYS.forEach((k) => usedKeys.add(k));
      usedKeys.add('membershiptier');
      usedKeys.add('customer first plan');
      usedKeys.add('membership_tier');
      usedKeys.add('tier');
      usedKeys.add('customer_first_plan_id');
      usedKeys.add('plan_id');
      usedKeys.add('customer_first_balance');
      usedKeys.add('plan_balance');
      usedKeys.add('customerfirstcreditlimit');
      usedKeys.add('credit_limit');
      usedKeys.add('plan_credit_limit');

      const value = preferenceValueForField(prefs, field, user, membership);
      const planId = resolvePlanId(prefs, membership);
      const credit = resolvePlanCreditLimit(prefs, membership);
      const enrolled =
        isCustomerFirstEnrolled(value) ||
        (Boolean(planId) && !['disable', 'no'].includes(String(value || '').toLowerCase()));

      rows.push({
        key: `${field.id || field.key || 'membershipTier'}-plan-id`,
        label: 'Plan ID',
        value: enrolled && planId ? planId : '—',
      });
      rows.push({
        key: `${field.id || field.key || 'membershipTier'}-credit`,
        label: 'Plan credit limit',
        value: enrolled && credit ? credit : '—',
      });
      return;
    }

    let value = preferenceValueForField(prefs, field, user, membership);
    if (field.type === 'dateOfBirth' || field.key === 'dateOfBirth') {
      usedKeys.add('date_of_birth');
      usedKeys.add('dateofbirth');
      usedKeys.add('dob');
      value = value ? formatDobDisplay(value) : '';
    }

    rows.push({
      key: field.id || field.key || welcomeLabelForField(field),
      label: welcomeLabelForField(field),
      value: value || '—',
    });
  });

  Object.entries(prefs).forEach(([key, value]) => {
    if (value == null || String(value).trim() === '') return;
    const lk = String(key).toLowerCase();
    if (HIDDEN_PREF_KEYS.has(lk) || HIDDEN_PREF_KEYS.has(key)) return;
    if (CFP_META_KEYS.has(lk) || CFP_META_KEYS.has(key)) return;
    if (['date_of_birth', 'dob', 'dateofbirth'].includes(lk)) return;
    if (usedKeys.has(lk)) return;
    if (isCustomerFirstEnrolled(value) || value === 'Disable' || value === 'No' || value === 'Yes') {
      return;
    }
    rows.push({
      key,
      label: humanizePrefKey(key),
      value: String(value).trim(),
    });
  });

  return rows;
}

/** Build editable draft.custom from profile for welcome preference editing. */
export function draftFromWelcomeProfile(preferences, registerForm, user = null, membership = null) {
  const form = normalizeRegisterForm(registerForm);
  const prefs = preferences && typeof preferences === 'object' ? preferences : {};
  const custom = {};

  enabledRegisterFields(form).forEach((field) => {
    const raw = preferenceValueForField(prefs, field, user, membership);
    if (field.type === 'membershipTier' || field.key === 'membershipTier') {
      const planId = resolvePlanId(prefs, membership);
      const credit = resolvePlanCreditLimit(prefs, membership);
      if (
        isCustomerFirstEnrolled(raw) ||
        (planId && !['disable', 'no'].includes(String(raw || '').toLowerCase()))
      ) {
        custom[field.key] = 'Yes';
        custom[CUSTOMER_FIRST_PLAN_ID_KEY] = planId;
        custom[CUSTOMER_FIRST_CREDIT_KEY] = credit;
      } else {
        custom[field.key] = 'No';
        custom[CUSTOMER_FIRST_PLAN_ID_KEY] = '';
        custom[CUSTOMER_FIRST_CREDIT_KEY] = '';
      }
      return;
    }
    custom[field.key] = raw || '';
  });

  return { custom };
}

/** Payload for PUT face user profile from welcome preference edits. */
export function buildWelcomeProfileUpdatePayload(draft, registerForm) {
  const form = normalizeRegisterForm(registerForm);
  const fields = enabledRegisterFields(form);
  const built = buildRegisterProfilePayload(draft, fields, form);
  const preferences = { ...(built.preferences || {}) };

  const cfpField = fields.find(
    (f) => f.type === 'membershipTier' || f.key === 'membershipTier',
  );
  let enrolled = false;
  let creditPoints;
  if (cfpField) {
    enrolled = isCustomerFirstEnrolled(draft?.custom?.[cfpField.key]);
    const credit = String(draft?.custom?.[CUSTOMER_FIRST_CREDIT_KEY] || '').trim();
    if (!enrolled) {
      preferences.membershipTier = 'No';
      preferences.customer_first_plan_name = 'No';
      preferences.customer_first_plan_id = '';
      preferences.plan_id = '';
      preferences.customer_first_balance = '';
      preferences.plan_balance = '';
      creditPoints = 0;
    } else if (credit) {
      preferences.customer_first_balance = credit;
      preferences.plan_balance = credit;
      const n = Number(credit);
      if (Number.isFinite(n)) creditPoints = Math.max(0, Math.floor(n));
    }
  }

  const genderField = fields.find((f) => f.type === 'gender' || f.key === 'gender');
  const gender = genderField
    ? String(draft?.custom?.[genderField.key] || '').trim()
    : String(draft?.custom?.gender || '').trim();

  return {
    name: built.name,
    mobile: built.mobile,
    ...(built.email ? { email: built.email } : {}),
    ...(built.dateOfBirth ? { dateOfBirth: built.dateOfBirth } : {}),
    ...(built.skinType ? { skinType: built.skinType } : {}),
    preferences,
    ...(gender ? { gender } : {}),
    membership: enrolled
      ? {
          tier: 'Customer First',
          member_id: built.memberId || '',
          ...(creditPoints != null ? { points: creditPoints } : {}),
        }
      : {
          member_id: '',
          points: 0,
        },
  };
}

export function validateWelcomePreferencesDraft(draft, registerForm) {
  return validateRegisterDraft({
    draft,
    fields: enabledRegisterFields(normalizeRegisterForm(registerForm)),
    hasCapture: true,
    registerForm,
  });
}
